// This is the entry point for the home component
/*eslint-disable */
import React, { FC, useCallback, useMemo, useContext, useEffect } from 'react';

// Redux
import { Dispatch } from 'redux';

// Context
import UserDetailsContext from 'Context/userDetailsContext';

// GraphQL
import { useQuery } from '@apollo/react-hooks';

// Styling
import injectSheet from 'react-jss';

// Actions

// Classes

// Components
import { FilterRadioHidden } from 'Components/FilterRadioHidden';
import CheckBox from 'Components/CheckBox';
import { FilterSlider } from 'Components/FilterSlider';
import MultiSelect from 'Components/MultiSelect';

// Utils
import { areEqual } from 'Utils/areEqual';
import { ProductAnalyticsContext } from 'Context/ProductAnalytics';
import withErrorBoundary from 'HOC/ErrorBoundary';
import { Range } from '../../classes/filter.classes';
import { EntryTerm } from '../classes/prospectProfile.classes';
import { setEntryTermValue } from '../actions/prospectProfile.actions';
import styleSheet from './styles';
import instiutionYearsGql from './gqlQuery';

interface Props {
  entryTerm: EntryTerm;
  dispatch: Dispatch<any>;
  classes: any;
}

const minutes = 1000 * 60;
const hours = minutes * 60;
const days = hours * 24;
const years = days * 365;

const EntryTermContainer: FC<Props> = (props: Props) => {
  const userDetails = useContext(UserDetailsContext);
  const date = new Date();
  const currYear = date.getFullYear();
  const endYear = currYear + 5;
  const {
    filterTrackingHelpers: {
      Listmembership: { listLabels },
      PropsectProfile: { termCheck },
    },
    actions: { filterTrack },
  } = useContext(ProductAnalyticsContext);

  const { data: institutionYearsData } = useQuery(instiutionYearsGql, {
    variables: { instId: userDetails.instId },
  });

  const { entryTerm, dispatch, classes } = props;

  useEffect(() => {
    /*
      Check the type of value.
      if it's an array, do the if

      if it's an object and spring or fall exists

    */
    if (entryTerm?.value?.length > 0) {
      // This is the array for Term-Year selections
      filterTrack({
        filterCategory: 'Prospect Profile',
        filterName: `Entry Term - Term-Year`,
        filterValue: listLabels(entryTerm.value),
        addToStore: true,
      });
    }
  }, [entryTerm]);

  const startDate =
    institutionYearsData &&
    institutionYearsData.institutions &&
    Date.parse(institutionYearsData.institutions.live_date);

  const yearsSince1970 = Math.round(startDate / years);
  const startYear = (yearsSince1970 && 1970 + yearsSince1970) || 2010;

  const entryTermCallback = (event: any) => {
    const { id, value = '' } = event.target;

    // Combined Analytics Event
    if (typeof value === 'string') {
      const valueLowerCase = value.toLocaleLowerCase();
      // Combined Analytics Event
      // This tracks the user selecting any in the filters.  It also deals with the changing types of value
      if (typeof value === 'string' && valueLowerCase === 'any') {
        filterTrack({
          filterCategory: 'Prospect Profile',
          filterName: `Entry Term - Any`,
          filterValue: 'Any',
          addToStore: true,
        });
      } else if (typeof value === 'string' && valueLowerCase === 'year') {
        filterTrack({
          filterCategory: 'Prospect Profile',
          filterName: `Entry Term - Year`,
          filterValue: '2010 - 2025', // Default value
          addToStore: true,
        });
      }
    }

    setEntryTermValue(dispatch, entryTerm, value, id, startYear, endYear);
  };

  const handleEntryTermCheckBoxClick = (event: any) => {
    const value = {
      ...entryTerm.value,
      [event.target.id]: !entryTerm.value[event.target.id],
    };

    // If termcheck returns false, we want to send an event.
    if (termCheck('spring', value.spring)) {
      filterTrack({
        filterCategory: 'Prospect Profile',
        filterName: `Entry Term - Term`,
        filterValue: 'spring',
        addToStore: true,
      });
    }
    if (termCheck('fall', value.fall)) {
      filterTrack({
        filterCategory: 'Prospect Profile',
        filterName: `EntryTerm - Term`,
        filterValue: 'fall',
        addToStore: true,
      });
    }
    setEntryTermValue(dispatch, entryTerm, value, 'value', startYear, endYear);
  };

  const entryTermRangeSliderChange = (values: number[]) => {
    const value = new Range(values[0], values[1]);
    // Combined Analytics Event
    filterTrack({
      filterCategory: 'Prospect Profile',
      filterName: `Entry Term - Year`,
      filterValue: `${values[0]} - ${values[1]}`,
      addToStore: true,
    });
    setEntryTermValue(dispatch, entryTerm, value, 'value', startYear, endYear);
  };

  const onDropdownChangeEntryTerm = (event: any) => {
    let newSelected = [...entryTerm.value];

    if (
      entryTerm.value.some((option: any) => {
        return areEqual(event.target.value, option);
      })
    ) {
      newSelected = newSelected.filter(option => {
        return !areEqual(event.target.value, option);
      });
    } else {
      newSelected.push(event.target.value);
    }

    const response = { target: { value: newSelected, id: 'value' } };
    entryTermCallback(response);
  };

  const mapEntryTerms = () => {
    const entryTerms = [];

    entryTerms.push({
      label: 'Undecided',
      value: 'Undecided',
    });

    for (let year = startYear; year <= endYear; year += 1) {
      entryTerms.push({
        label: `Spring ${year}`,
        value: `Spring ${year}`,
      });
      entryTerms.push({
        label: `Fall ${year}`,
        value: `Fall ${year}`,
      });
    }

    return entryTerms;
  };

  const memoizedEntryTerms = useMemo(() => mapEntryTerms(), [startYear]);

  return (
    <div className={classes.container}>
      <FilterRadioHidden
        label="Any"
        value="Any"
        selected={entryTerm.selection === 'Any'}
        identifier="selectionProspectProfileEntryTerm"
        onClick={useCallback(entryTermCallback, [entryTerm, startYear])}
      />
      <FilterRadioHidden
        label="Term-Year"
        value="Term-Year"
        selected={entryTerm.selection === 'Term-Year'}
        identifier="selectionProspectProfileEntryTerm"
        onClick={useCallback(entryTermCallback, [entryTerm, startYear])}
        overrideStyles={{
          marginLeft: '27px',
        }}
      >
        <MultiSelect
          selectedOptions={
            (entryTerm.selection === 'Term-Year' && entryTerm.value) || []
          }
          items={memoizedEntryTerms}
          onDropdownChange={useCallback(onDropdownChangeEntryTerm, [
            entryTerm,
            startYear,
          ])}
          selectionDescription={
            (entryTerm.selection === 'Term-Year' &&
              entryTerm.value.length > 1 &&
              'Terms') ||
            'Term'
          }
          placeholder="Select Term-Year"
        />
      </FilterRadioHidden>
      <FilterRadioHidden
        label="Term"
        value="Term"
        selected={entryTerm.selection === 'Term'}
        identifier="selectionProspectProfileEntryTerm"
        onClick={useCallback(entryTermCallback, [entryTerm, startYear])}
      >
        <CheckBox
          label="Fall"
          id="fall"
          value={
            (entryTerm.selection === 'Term' && entryTerm.value.fall) || false
          }
          isChecked={
            (entryTerm.selection === 'Term' && entryTerm.value.fall) || false
          }
          onClick={useCallback(handleEntryTermCheckBoxClick, [
            entryTerm,
            startYear,
          ])}
          overrideStyles={{ margin: '8px 0 17px' }}
        />
        <CheckBox
          label="Spring"
          id="spring"
          value={
            (entryTerm.selection === 'Term' && entryTerm.value.spring) || false
          }
          isChecked={
            (entryTerm.selection === 'Term' && entryTerm.value.spring) || false
          }
          onClick={useCallback(handleEntryTermCheckBoxClick, [
            entryTerm,
            startYear,
          ])}
          overrideStyles={{ margin: '0 0 8px' }}
        />
      </FilterRadioHidden>
      <FilterRadioHidden
        label="Year"
        value="Year"
        selected={entryTerm.selection === 'Year'}
        identifier="selectionProspectProfileEntryTerm"
        onClick={useCallback(entryTermCallback, [entryTerm, startYear])}
        overrideStyles={{
          marginLeft: '27px',
        }}
      >
        <FilterSlider
          defaultValues={{
            handleOne:
              (entryTerm.selection === 'Year' && entryTerm.value.lower) ||
              startYear,
            handleTwo:
              (entryTerm.selection === 'Year' && entryTerm.value.upper) ||
              endYear,
          }}
          // need to know where to get this domain
          range={{ lowerBound: startYear, upperBound: endYear }}
          step={1}
          onSlideEnd={useCallback(entryTermRangeSliderChange, [
            entryTerm,
            startYear,
          ])}
          ariaLabel="Select Entry Year Range"
        />
      </FilterRadioHidden>
    </div>
  );
};

export default withErrorBoundary(injectSheet(styleSheet)(EntryTermContainer));
